import { Navigate } from "react-router-dom";
import moment from "moment";
import { logout, refreshUserSession, updateUserAttribute } from "../authenticate";
import { useEffect, useState } from "react";
import SessionTimeoutModal from "../../components/modals/SessionTimeoutModal";
import { getUser } from "../connector/userQueries";
import { getUserPool } from "../pools/pools";
import { Box, CircularProgress } from "@mui/material";
import { getAuth, setAuth } from "../../contexts/authStore";
import { getSubscription, getUserPaymentMethod } from "../connector/paypalQueries";
import { SUBSCRIPTION_PLANS } from "../../pages/ProfileSettings/ProfileSettings";

export const FREE_TIER_EXPIRY = 30; // Number of days before free tier expires

export function validateAuth(): boolean {
  const expiry = getAuth().expiry!;
  const refreshExpiry = getAuth().refreshExpiry!;
  try {
    if (expiry && moment(expiry) > moment()) {
      return true
    }
    else if (expiry && moment(expiry) <= moment()) {
      if (refreshExpiry && moment(refreshExpiry) <= moment()) {
        logout()
        return false
      } else {
        refreshUserSession()
        return true
      }
    }
    else {
      return false
    }
  } catch (error) {
    return false;
  }
}

export function idToken(): any {
  const idToken = getAuth().idToken!;
  return idToken
}

export default function Auth({ children }: { children?: React.ReactNode }) {
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(validateAuth());

  useEffect(() => {
    if (authenticated) {
      setIsValidating(true);
      const userId = getAuth().userId;
      if (!userId) {
        setIsValidating(false);
        return;
      }
      getUser(userId).then((userData) => {
        try {
          getUserPaymentMethod(userData[0].userId).then((paymentMethod) => {
            if (!paymentMethod || paymentMethod.statusCode === 404) {
              updateUserAttribute("custom:access_level", "free");
              setAuth({ ...getAuth(), access_level: "free" } as any);
              setIsValidating(false);
              return;
            }
            paymentMethod = JSON.parse(paymentMethod.body)
            if (paymentMethod.activeSubscriptionId != null) {
              getSubscription(userData[0].userId, paymentMethod.activeSubscriptionId).then((subscription) => {
                const subscriptionBody = JSON.parse(subscription.body);
                if (subscriptionBody.status === "Active" || subscriptionBody.status === "Pending") {
                  const matchedPlan = SUBSCRIPTION_PLANS.find(plan => plan.planId === subscriptionBody.planId);
                  const accessLevel = matchedPlan ? matchedPlan.id : "free";
                  updateUserAttribute("custom:access_level", accessLevel);
                  setAuth({ ...getAuth(), access_level: accessLevel } as any);
                } else {
                  updateUserAttribute("custom:access_level", "free");
                  setAuth({ ...getAuth(), access_level: "free" } as any);
                }
              });
            } else {
              updateUserAttribute("custom:access_level", "free");
              setAuth({ ...getAuth(), access_level: "free" } as any);
            }
            setIsValidating(false);
          });
        } catch (error) {
          setIsValidating(false);
        }

      })
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setAuthenticated(validateAuth());
    }, 60_000); // 1 minute

    return () => clearInterval(intervalId);
  }, []);

  const handleStillActive = () => {
    // Refresh session token or extend session
  };

  const handleTimeout = () => {
    // Clear session and redirect to login
    logout();
  };

  if (authenticated && isValidating) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          zIndex: 9999
        }}
      >
        <CircularProgress size={120} color="info" />
      </Box>
    );
  }

  return authenticated ? (
    <>
      {children}
      <SessionTimeoutModal
        warningTime={10 * 60 * 1000}  // 10 minutes
        countdownTime={2 * 60 * 1000}  // 2 minutes
        onStillActive={handleStillActive}
        onTimeout={handleTimeout}
      />
    </>
  ) : (
    <Navigate to="/login" replace />
  );
}